import { X, Calendar, MapPin, Users, MessageSquare, DollarSign, Shield, Flame, Phone, Mail } from 'lucide-react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import "@/styles/fj.css";
import { roleColor, initial, avatarGradient } from "@/utils/fjMeta";
import linkifyText from '../utils/linkifyText';

const JobDetailsModal = ({ job, isOpen, onClose, isDark = false, showApplyButton = false, onApply, onViewInvoice, onAskQuestion, onMessageOwner, onMessageCrewMember, showCrewPayout = false, hideHiredCrew = false, messagesContext = false, isJobOwner = false, wasHired = null }) => {

  // iOS-safe body scroll lock
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    }

    return () => {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    };
  }, [isOpen]);

  if (!isOpen || !job) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const getStatusConfig = (status) => {
    const configs = {
      PENDING_APPROVAL: { color: 'bg-yellow-500', label: 'Pending', textColor: 'text-yellow-600' },
      SEEKING_APPLICANTS: { color: 'bg-emerald-500', label: 'Seeking', textColor: 'text-emerald-600' },
      OFFER_SENT: { color: 'bg-orange-500', label: 'Offer Sent', textColor: 'text-orange-600' },
      HIRED: { color: 'bg-blue-500', label: 'Hired', textColor: 'text-blue-600' },
      PENDING_COMPLETION: { color: 'bg-indigo-500', label: 'Pending', textColor: 'text-indigo-600' },
      RELEASE_PAYMENT: { color: 'bg-cyan-500', label: 'Payment', textColor: 'text-cyan-600' },
      PAID_COMPLETED: { color: 'bg-green-600', label: 'Completed', textColor: 'text-green-600' },
      CANCELLED: { color: 'bg-red-500', label: 'Cancelled', textColor: 'text-red-600' },
      open: { color: 'bg-emerald-500', label: 'Seeking', textColor: 'text-emerald-600' },
      active: { color: 'bg-emerald-500', label: 'Seeking', textColor: 'text-emerald-600' },
      pending_hire: { color: 'bg-orange-500', label: 'Offer Sent', textColor: 'text-orange-600' },
      hired: { color: 'bg-blue-500', label: 'Hired', textColor: 'text-blue-600' },
      completed: { color: 'bg-green-600', label: 'Completed', textColor: 'text-green-600' },
      cancelled: { color: 'bg-red-500', label: 'Cancelled', textColor: 'text-red-600' }
    };
    return configs[status] || configs.SEEKING_APPLICANTS;
  };

  const getCrewPayout = (amount) => Math.floor(amount * 0.98);

  const displayRate = showCrewPayout ? getCrewPayout(job.ratePerHour || job.pay_rate || job.job_rate || 0) : (job.ratePerHour || job.pay_rate || job.job_rate || 0);
  const displayTotal = showCrewPayout
    ? (job.crew_payout || getCrewPayout(job.total || job.total_amount || job.job_total || 0))
    : (job.total || job.total_amount || job.job_total || 0);

  const statusConfig = getStatusConfig(job.status);

  const isAdvancedJob = job.is_advanced_job !== undefined
    ? job.is_advanced_job
    : ((job.dates && job.dates.length > 1) || (job.crew_count && job.crew_count > 1) || (job.crew_members && job.crew_members.length > 1));
  const jobDates = job.dates || [job.date || job.job_date];
  const crewMembers = job.crew_members || [];

  // Header band: owner/green context = Green→Cyan; everything else = violet→magenta (Command Deck).
  const headerBand = isJobOwner
    ? 'linear-gradient(120deg, #15803D, #0E7490)'
    : 'linear-gradient(120deg, #572B7C, #78287C)';

  const showStatusPill = (!messagesContext || job.status === 'SEEKING_APPLICANTS' || job.status === 'open' || job.status === 'active');
  const ownerName = job.owner?.name || job.owner_name || job.poster_name;
  const ownerContactVisible = (job.owner?.phone || job.owner?.email) && ['HIRED', 'PENDING_COMPLETION', 'RELEASE_PAYMENT'].includes(job.status);

  // Command Deck dark palette — force the dark tokens regardless of app theme (inline only).
  const cdVars = {
    '--fj-card': '#12162A',
    '--fj-card2': 'rgba(255,255,255,.045)',
    '--fj-text': '#FFFFFF',
    '--fj-text2': 'rgba(255,255,255,.72)',
    '--fj-muted': 'rgba(255,255,255,.5)',
    '--fj-line': 'rgba(255,255,255,.12)',
    '--fj-line2': 'rgba(255,255,255,.09)',
    '--fj-chip': 'rgba(255,255,255,.07)',
    '--fj-inputbg': 'rgba(255,255,255,.05)',
    '--fj-inputbd': 'rgba(255,255,255,.14)',
    '--fj-earn-fg': '#67C6B4',
    '--fj-earn-bg': 'rgba(103,198,180,.10)',
    '--fj-wwcc-fg': '#8FB0FF',
    '--fj-wwcc-bg': 'rgba(59,130,246,.18)',
    '--fj-pop-fg': '#FBBF24',
    '--fj-pop-bg': 'rgba(251,191,36,.15)',
  };
  const cdBtnAqua = { background: '#28C0D0', color: '#0C1120', border: 'none', boxShadow: '0 12px 28px -10px rgba(40,192,208,.55)' };
  const cdBtnGhost = { background: 'rgba(255,255,255,.06)', borderColor: 'rgba(255,255,255,.16)', color: '#fff' };

  return createPortal(
    <div
      className="fj-root fj-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ ...cdVars, zIndex: 4000, touchAction: 'none', padding: 20, background: 'rgba(8,11,20,.66)', backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)' }}
    >
      <div
        className="fj-modal-card fjscroll"
        style={{ touchAction: 'pan-y', background: '#12162A', border: '1px solid rgba(255,255,255,.12)', boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)', maxWidth: 720 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header band */}
        <div className="relative flex-shrink-0 px-6 py-5 text-white" style={{ background: headerBand }}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-[9px] bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all"
            style={{ border: '1px solid rgba(255,255,255,.18)' }}
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 text-xs opacity-90 mb-2 flex-wrap pr-8">
            <span className="fj-mono bg-white/20 px-2 py-0.5 rounded-md">
              {job.jobNumber || job.job_number || job.id?.slice(0, 8).toUpperCase() || 'JOB'}
            </span>
            <span className="font-semibold capitalize">
              {(job.type || job.job_type || 'Job').replace('_', ' ')}
            </span>
            {showStatusPill && (
              <span className="bg-white/20 px-2.5 py-0.5 rounded-full font-semibold">
                {statusConfig.label === 'Seeking' ? 'Seeking applicants' : statusConfig.label}
              </span>
            )}
          </div>
          <h2 className="fj-h1 font-bold text-[22px] leading-tight tracking-[-0.4px]">
            {job.title || job.job_title || 'Job Details'}
          </h2>
          <div className="flex items-center gap-1.5 mt-2.5 text-sm opacity-90">
            <Calendar className="w-3.5 h-3.5" />
            <span>{formatDate(job.jobDate || job.date || job.job_date)}</span>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto fjscroll" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="p-6 space-y-4">

            {/* Crew Role Banner */}
            {showCrewPayout && job.crew_role && (
              <div className="rounded-xl px-4 py-3 text-center fj-badge-earn" style={{ display: 'block' }}>
                <p className="fj-overline mb-0.5">You are working as</p>
                <p className="fj-h3 text-lg font-bold uppercase tracking-wide">
                  {job.crew_role.replace(/_/g, ' ')}
                </p>
              </div>
            )}

            {/* Messages Context Banner */}
            {messagesContext && (
              <div
                className="rounded-xl px-4 py-3 text-center"
                style={
                  isJobOwner || wasHired === true
                    ? { background: 'var(--fj-earn-bg)' }
                    : wasHired === false
                      ? { background: 'rgba(244,63,94,.12)' }
                      : { background: 'var(--fj-card2)', border: '1px solid var(--fj-line2)' }
                }
              >
                <p
                  className="text-sm font-bold"
                  style={{
                    color: (isJobOwner || wasHired === true)
                      ? 'var(--fj-earn-fg)'
                      : wasHired === false ? '#F43F5E' : 'var(--fj-muted)'
                  }}
                >
                  {isJobOwner ? 'You posted this job' : wasHired === true ? 'You were hired for this job' : wasHired === false ? 'You were not hired for this job' : 'Job Details'}
                </p>
              </div>
            )}

            {/* Applicants pill */}
            {job.applicants !== undefined && job.applicants > 0 && !job.hiredCrew && (
              <div
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12.5px] font-semibold ${job.applicants >= 5 ? 'fj-badge-pop' : 'fj-chip'}`}
              >
                {job.applicants >= 5 ? <Flame className="w-3.5 h-3.5 fj-flame" /> : <Users className="w-3.5 h-3.5" />}
                <span>{job.applicants} {job.applicants === 1 ? 'applicant' : 'applicants'}</span>
                {job.applicants >= 5 && <span>· Popular</span>}
              </div>
            )}

            {/* Stats Grid — 4 columns */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
              <div className="fj-card2-tile rounded-[13px] p-3" style={{ background: 'var(--fj-card2)', border: '1px solid var(--fj-line2)' }}>
                <p className="fj-overline text-[11px] tracking-[0.4px]" style={{ color: 'var(--fj-muted)' }}>
                  {isAdvancedJob && jobDates.length > 1 ? 'Dates' : 'Date'}
                </p>
                <p className="fj-num font-bold text-[17px] tracking-[-0.3px] mt-0.5" style={{ color: 'var(--fj-text)' }}>
                  {isAdvancedJob && jobDates.length > 1 ? `${jobDates.length} days` : formatDate(job.jobDate || job.date || job.job_date)}
                </p>
              </div>
              <div className="rounded-[13px] p-3" style={{ background: 'var(--fj-card2)', border: '1px solid var(--fj-line2)' }}>
                <p className="fj-overline text-[11px] tracking-[0.4px]" style={{ color: 'var(--fj-muted)' }}>
                  {isAdvancedJob ? 'Total Hours' : 'Duration'}
                </p>
                <p className="fj-num font-bold text-[17px] tracking-[-0.3px] mt-0.5" style={{ color: 'var(--fj-text)' }}>
                  {isAdvancedJob && crewMembers.length > 0
                    ? `${crewMembers.reduce((sum, m) => sum + (m.hours || 0), 0)}h`
                    : `${job.hours || job.job_hours || 0}h`}
                </p>
              </div>
              <div className="rounded-[13px] p-3" style={{ background: 'var(--fj-card2)', border: '1px solid var(--fj-line2)' }}>
                <p className="fj-overline text-[11px] tracking-[0.4px]" style={{ color: 'var(--fj-muted)' }}>
                  {isAdvancedJob ? 'Crew' : 'Rate'}
                </p>
                <p className="fj-num font-bold text-[17px] tracking-[-0.3px] mt-0.5" style={{ color: 'var(--fj-text)' }}>
                  {isAdvancedJob ? `${job.crew_count || crewMembers.length} needed` : `$${displayRate}/hr`}
                </p>
              </div>
              <div className="rounded-[13px] p-3" style={{ background: 'var(--fj-card2)', border: '1px solid var(--fj-line2)' }}>
                <p className="fj-overline text-[11px] tracking-[0.4px]" style={{ color: 'var(--fj-earn-fg)' }}>
                  {showCrewPayout ? 'Your Payout' : 'You earn'}
                </p>
                <p className="fj-num font-bold text-[17px] tracking-[-0.3px] mt-0.5" style={{ color: 'var(--fj-earn-fg)' }}>
                  ${isAdvancedJob && job.budget ? job.budget.toLocaleString() : displayTotal.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Hired Crew — Advanced Jobs */}
            {isAdvancedJob && crewMembers.some(cm => cm.hired || cm.hired_user_id || cm.hiredUserName) && !hideHiredCrew && (
              <div>
                <h3 className="fj-overline mb-3" style={{ color: 'var(--fj-muted)' }}>
                  Hired Crew ({crewMembers.filter(cm => cm.hired || cm.hired_user_id || cm.hiredUserName).length}/{crewMembers.length})
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {crewMembers.map((member, idx) => {
                    const isHired = member.hired || member.hired_user_id || member.hiredUserName;
                    const hiredName = member.hired_name || member.hiredUserName || null;
                    const hiredEmail = member.hired_email || member.hiredUserEmail || null;
                    const hiredPhoto = member.hiredUserPhoto || member.hired_photo || null;
                    return (
                      <div key={idx} className="flex items-center gap-3 p-3 rounded-[13px]" style={{ background: 'var(--fj-card2)', border: '1px solid var(--fj-line2)' }}>
                        <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0" style={{ border: `2px solid ${isHired ? 'var(--fj-earn-fg)' : 'var(--fj-line)'}` }}>
                          {isHired && hiredPhoto ? (
                            <img src={hiredPhoto} alt={hiredName || ''} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
                          ) : null}
                          <div className="w-full h-full flex items-center justify-center text-white text-xs font-bold" style={{ display: (isHired && hiredPhoto) ? 'none' : 'flex', background: isHired ? '#22C55E' : 'var(--fj-muted)' }}>
                            {isHired && hiredName ? hiredName.charAt(0) : '?'}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold capitalize" style={{ color: 'var(--fj-text)' }}>
                              {member.role?.replace(/_/g, ' ') || 'Crew'}
                            </span>
                            {isHired && <span className="text-xs font-bold" style={{ color: 'var(--fj-earn-fg)' }}>Hired</span>}
                            {!isHired && <span className="fj-chip text-[10px]">Open</span>}
                          </div>
                          {isHired && hiredName && (
                            <p className="text-xs" style={{ color: 'var(--fj-earn-fg)' }}>{hiredName}</p>
                          )}
                          <p className="text-[11px]" style={{ color: 'var(--fj-muted)' }}>{member.hours}h x ${member.ratePerHour}/hr</p>
                          {isHired && (
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              {onMessageCrewMember && hiredEmail && (
                                <button onClick={(e) => { e.stopPropagation(); onMessageCrewMember(hiredEmail); }} className="fj-btn fj-btn-primary !py-1 !px-2 !text-[10px] !rounded-md" style={cdBtnAqua}>
                                  <MessageSquare className="w-2.5 h-2.5" /> Message
                                </button>
                              )}
                              {(member.hiredUserPhone || member.hired_phone) && (
                                <a href={`tel:${member.hiredUserPhone || member.hired_phone}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[10px] font-medium fj-badge-wwcc">
                                  <Phone className="w-2.5 h-2.5" /> Phone
                                </a>
                              )}
                              {hiredEmail && (
                                <a href={`mailto:${hiredEmail}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[10px] font-medium fj-chip">
                                  <Mail className="w-2.5 h-2.5" /> Email
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                        <span className="fj-num text-sm font-bold" style={{ color: isHired ? 'var(--fj-earn-fg)' : 'var(--fj-muted)' }}>
                          ${member.subtotal?.toFixed(2) || (member.hours * member.ratePerHour).toFixed(2)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Hired Crew — Simple Job */}
            {!isAdvancedJob && job.hiredCrew && !hideHiredCrew && (
              <div className="flex items-center gap-3 p-3 rounded-[13px]" style={{ background: 'var(--fj-earn-bg)' }}>
                <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0" style={{ border: '1px solid var(--fj-earn-fg)' }}>
                  {job.hiredCrew.photo && job.hiredCrew.photo !== 'https://via.placeholder.com/150' ? (
                    <img src={job.hiredCrew.photo} alt={job.hiredCrew.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white font-bold text-sm" style={{ background: '#22C55E' }}>
                      {job.hiredCrew.name.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold" style={{ color: 'var(--fj-text)' }}>{job.hiredCrew.name}</p>
                  <p className="text-xs" style={{ color: 'var(--fj-muted)' }}>{job.hiredCrew.role || 'Crew Member'}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  {job.hiredCrew.phone && (
                    <a href={`tel:${job.hiredCrew.phone}`} onClick={(e) => e.stopPropagation()} className="p-1.5 rounded-lg fj-badge-earn">
                      <Phone className="w-3.5 h-3.5" />
                    </a>
                  )}
                  {job.hiredCrew.email && (
                    <a href={`mailto:${job.hiredCrew.email}`} onClick={(e) => e.stopPropagation()} className="p-1.5 rounded-lg fj-badge-earn">
                      <Mail className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Posted by + Location */}
            <div className="flex items-center gap-3 flex-wrap">
              {ownerName && (
                <div className="flex items-center gap-2.5 flex-1 min-w-[180px]">
                  <div className="w-9 h-9 rounded-[10px] overflow-hidden flex items-center justify-center text-white fj-name font-bold text-[13px] flex-shrink-0" style={{ background: avatarGradient(ownerName) }}>
                    {job.owner?.photo ? (
                      <img src={job.owner.photo} alt={ownerName} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
                    ) : null}
                    <span style={{ display: job.owner?.photo ? 'none' : 'flex' }}>{initial(ownerName)}</span>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10.5px]" style={{ color: 'var(--fj-muted)' }}>Posted by</div>
                    <div className="text-[13px] font-semibold truncate" style={{ color: 'var(--fj-text)' }}>{ownerName}</div>
                    {ownerContactVisible && (
                      <div className="flex gap-1.5 mt-1">
                        {job.owner.phone && (
                          <a href={`tel:${job.owner.phone}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium fj-badge-earn">
                            <Phone className="w-3 h-3" /> Phone
                          </a>
                        )}
                        {job.owner.email && (
                          <a href={`mailto:${job.owner.email}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium fj-badge-earn">
                            <Mail className="w-3 h-3" /> Email
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Location */}
            <div className="flex items-start gap-2 text-[13.5px]" style={{ color: 'var(--fj-text2)' }}>
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--fj-magenta)' }} />
              <span>{job.location || job.location_address || job.job_location || 'Not specified'}</span>
            </div>

            {/* Job brief / Description */}
            {(job.brief || job.description) && (
              <div>
                <div className="fj-overline mb-1.5" style={{ color: 'var(--fj-muted)' }}>Job brief</div>
                <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--fj-text2)' }}>
                  {linkifyText(job.brief || job.description)}
                </p>
              </div>
            )}

            {/* Advanced Job Details */}
            {isAdvancedJob && (
              <div className="rounded-[13px] p-4" style={{ background: 'var(--fj-card2)', border: '1px solid var(--fj-line2)' }}>
                <h3 className="fj-overline mb-3" style={{ color: 'var(--fj-muted)' }}>Available positions</h3>

                {jobDates.length > 1 && (
                  <div className="mb-3">
                    <p className="text-[11px] font-semibold mb-1.5" style={{ color: 'var(--fj-muted)' }}>Dates ({jobDates.length}):</p>
                    <div className="flex flex-wrap gap-1.5">
                      {jobDates.map((date, idx) => (
                        <span key={idx} className="fj-chip">{formatDate(date)}</span>
                      ))}
                    </div>
                  </div>
                )}

                {crewMembers.length > 0 && (
                  <div>
                    <div className="space-y-2">
                      {crewMembers.map((member, idx) => (
                        <div key={idx} className="p-3 rounded-[12px]" style={{ background: 'var(--fj-card)', border: '1px solid var(--fj-line)' }}>
                          <div className="flex items-center gap-2.5">
                            <span className="fj-dot" style={{ background: roleColor(member.role) }} />
                            <p className="fj-name text-sm font-semibold capitalize flex-1" style={{ color: 'var(--fj-text)' }}>{member.role?.replace(/_/g, ' ') || 'Crew'}</p>
                            {(member.hired || member.hired_user_id || member.hiredUserName) && (
                              <span className="fj-chip text-[11px]">Filled</span>
                            )}
                            <span className="fj-num text-base font-bold tracking-[-0.3px]" style={{ color: 'var(--fj-text)' }}>${member.subtotal?.toFixed(2) || (member.hours * member.ratePerHour).toFixed(2)}</span>
                          </div>
                          <div className="flex items-center gap-3.5 mt-1.5 text-xs" style={{ color: 'var(--fj-muted)' }}>
                            <span>{member.hours} hrs</span>
                            <span>${member.ratePerHour}/hr</span>
                            {member.dateAssignments && member.dateAssignments.length > 0 && (
                              <span>{member.dateAssignments.length} day{member.dateAssignments.length > 1 ? 's' : ''}</span>
                            )}
                          </div>
                          {member.dateAssignments && member.dateAssignments.length > 0 && (
                            <div className="mt-2 pt-2 space-y-1" style={{ borderTop: '1px solid var(--fj-line2)' }}>
                              {member.dateAssignments.map((a, i) => (
                                <div key={i} className="flex items-center justify-between text-[11px]">
                                  <span style={{ color: 'var(--fj-muted)' }}>{formatDate(a.date)}: {a.hours}h x ${a.ratePerHour}/hr</span>
                                  <span className="font-bold" style={{ color: 'var(--fj-text2)' }}>${a.subtotal?.toFixed(2) || (a.hours * a.ratePerHour).toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex items-center justify-between px-3 py-2.5 rounded-[12px]" style={{ background: 'var(--fj-earn-bg)' }}>
                      <span className="fj-overline" style={{ color: 'var(--fj-earn-fg)' }}>Total Budget</span>
                      <span className="fj-num text-base font-bold" style={{ color: 'var(--fj-earn-fg)' }}>
                        ${job.budget?.toFixed(2) || crewMembers.reduce((sum, m) => sum + (m.subtotal || 0), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Payment Breakdown */}
            <div className="rounded-[13px] p-4" style={{ background: 'var(--fj-card2)', border: '1px solid var(--fj-line2)' }}>
              <div className="flex justify-between items-center text-[13.5px] mb-1.5" style={{ color: 'var(--fj-text2)' }}>
                <span>Rate</span>
                <span>${displayRate}/hr</span>
              </div>
              <div className="flex justify-between items-center text-[13.5px]" style={{ color: 'var(--fj-text2)' }}>
                <span>Hours</span>
                <span>{job.hours || job.job_hours || 0}h</span>
              </div>
              <div className="my-2" style={{ height: '1px', background: 'var(--fj-line2)' }} />
              <div className="flex justify-between items-center">
                <span className="text-[13px] font-bold" style={{ color: 'var(--fj-earn-fg)' }}>
                  {showCrewPayout ? 'Your Payout' : 'You earn'}
                </span>
                <span className="fj-num text-[22px] font-bold tracking-[-0.4px]" style={{ color: 'var(--fj-text)' }}>${displayTotal.toLocaleString()}</span>
              </div>
            </div>

            {/* WWCC strip */}
            {job.requiresWWCC && (
              <div className="flex items-center gap-2.5 rounded-xl px-3.5 py-3 fj-badge-wwcc">
                <Shield className="w-4 h-4 flex-shrink-0" />
                <span className="text-[13px] font-semibold">Working With Children Check required</span>
              </div>
            )}

            {/* Required Roles */}
            {job.requiredRoles && job.requiredRoles.length > 0 && (
              <div>
                <h3 className="fj-overline mb-2" style={{ color: 'var(--fj-muted)' }}>Required Roles</h3>
                <div className="flex flex-wrap gap-1.5">
                  {job.requiredRoles.map((role, idx) => (
                    <span key={idx} className="fj-chip capitalize">
                      <span className="fj-dot" style={{ background: roleColor(role) }} />
                      {role.replace('_', ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="flex-shrink-0 px-6 py-4" style={{ borderTop: '1px solid var(--fj-line)', background: 'var(--fj-card)' }}>
          {showApplyButton && onApply ? (
            <div className="flex gap-2.5">
              {onAskQuestion ? (
                <button onClick={onAskQuestion} className="fj-btn fj-btn-outline flex-1" style={cdBtnGhost}>
                  <MessageSquare className="w-4 h-4" /> Ask a Question
                </button>
              ) : (
                <button onClick={onClose} className="fj-btn fj-btn-outline flex-shrink-0" style={cdBtnGhost}>
                  Close
                </button>
              )}
              <button onClick={onApply} className="fj-btn fj-btn-primary flex-1" style={cdBtnAqua}>
                Apply now
              </button>
            </div>
          ) : (
            <div className="flex gap-2.5">
              {onMessageOwner && !(isJobOwner && isAdvancedJob && crewMembers.some(cm => cm.hired || cm.hired_user_id || cm.hiredUserName)) && (
                <button onClick={onMessageOwner} className="fj-btn fj-btn-primary flex-1" style={cdBtnAqua}>
                  <MessageSquare className="w-4 h-4" /> Send Message
                </button>
              )}
              {!messagesContext && (job.status === 'PAID_COMPLETED' || job.status === 'completed' || job.status === 'paid') && job.invoice_id && onViewInvoice && (
                <button onClick={onViewInvoice} className="fj-btn fj-btn-ink flex-1" style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.16)', color: '#fff' }}>
                  <DollarSign className="w-4 h-4" /> Invoice
                </button>
              )}
              <button onClick={onClose} className="fj-btn fj-btn-outline flex-1" style={cdBtnGhost}>
                Close
              </button>
            </div>
          )}
          <p className="text-[8px] text-center py-1 opacity-20 select-none">modal:job-details</p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default JobDetailsModal;
